import { useEffect, useState } from "react";
import { Loader2, Check, X, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { formatHuf } from "@/components/SalaryFormSheet";
import { formatDateHu } from "@/components/DeductionFormDialog";

/**
 * Read-only detail overlay for a Bónusz or Jutalék record.
 * When the item is in `waiting_approval`, the user can approve or reject it
 * (rejection requires a reason). After paid/rejected/approved the dialog is
 * pure read-only.
 */
export function EarningsDetailDialog({
  open,
  onOpenChange,
  item,
  StatusBadge,
  testIdPrefix, // "bonus" | "commission"
  labels,
  approving,
  rejecting,
  onApprove,
  onReject,
  onDelete,
}) {
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectErr, setRejectErr] = useState("");

  useEffect(() => {
    if (!open) {
      setRejectMode(false);
      setRejectReason("");
      setRejectErr("");
    }
  }, [open]);

  if (!item) return null;
  const isWaiting = item.status === "waiting_approval";

  const submitReject = async () => {
    const reason = rejectReason.trim();
    if (!reason) {
      setRejectErr("Az elutasítás indoka kötelező.");
      return;
    }
    setRejectErr("");
    const ok = await onReject(item, reason);
    if (ok) {
      setRejectMode(false);
      setRejectReason("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={`${testIdPrefix}-detail-dialog`}
        className="sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2.5">
            <span>{labels.detailTitle}</span>
            <StatusBadge value={item.status} />
          </DialogTitle>
          <DialogDescription>{labels.detailDescription}</DialogDescription>
        </DialogHeader>

        <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <InfoRow label="Bruttó összeg" wide>
            <span className="font-mono text-base font-semibold">
              {formatHuf(item.amount)} Ft
            </span>
          </InfoRow>
          <InfoRow label="Kiadás ideje">{formatDateHu(item.issued_at)}</InfoRow>
          <InfoRow label="Kiadó">{item.issuer || "—"}</InfoRow>
          <InfoRow label="Indok" wide>
            <span className="whitespace-pre-wrap">{item.reason || "—"}</span>
          </InfoRow>

          {item.status === "approved" && item.approved_by_name && (
            <InfoRow label="Jóváhagyta" wide>
              {item.approved_by_name}
              {item.approved_at && (
                <span className="ml-1 text-muted-foreground">
                  · {formatDateHu(item.approved_at)}
                </span>
              )}
            </InfoRow>
          )}
          {item.status === "paid" && (
            <InfoRow label="Kifizetve" wide>
              {item.paid_at ? formatDateHu(item.paid_at) : "—"}
              {item.paid_via_salary_id && (
                <span className="ml-1 text-muted-foreground">
                  · bér rögzítéssel
                </span>
              )}
            </InfoRow>
          )}
          {item.status === "rejected" && (
            <>
              <InfoRow label="Elutasítva" wide>
                {item.rejected_by_name || "—"}
                {item.rejected_at && (
                  <span className="ml-1 text-muted-foreground">
                    · {formatDateHu(item.rejected_at)}
                  </span>
                )}
              </InfoRow>
              <InfoRow label="Elutasítás indoka" wide>
                <span className="whitespace-pre-wrap text-rose-700 dark:text-rose-400">
                  {item.reject_reason || "—"}
                </span>
              </InfoRow>
            </>
          )}
        </dl>

        {isWaiting && rejectMode && (
          <div className="space-y-2 rounded-md border border-rose-200 bg-rose-50/40 p-3 dark:border-rose-500/25 dark:bg-rose-500/5">
            <label
              htmlFor={`${testIdPrefix}-reject-reason`}
              className="block text-xs font-medium text-rose-700 dark:text-rose-400"
            >
              Elutasítás indoka *
            </label>
            <Textarea
              id={`${testIdPrefix}-reject-reason`}
              data-testid={`${testIdPrefix}-reject-reason`}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder="Pl. nem teljesült a feltétel."
            />
            {rejectErr && (
              <p
                data-testid={`${testIdPrefix}-reject-error`}
                className="text-xs text-destructive"
              >
                {rejectErr}
              </p>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  setRejectMode(false);
                  setRejectReason("");
                  setRejectErr("");
                }}
              >
                Mégse
              </Button>
              <Button
                type="button"
                size="sm"
                variant="destructive"
                data-testid={`${testIdPrefix}-reject-confirm`}
                onClick={submitReject}
                disabled={rejecting}
              >
                {rejecting ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <X className="h-3.5 w-3.5" />
                )}
                Elutasítás megerősítése
              </Button>
            </div>
          </div>
        )}

        <DialogFooter className={cn("gap-2", isWaiting ? "sm:justify-between" : "")}>
          {isWaiting && !rejectMode ? (
            <>
              <Button
                type="button"
                variant="outline"
                data-testid={`${testIdPrefix}-detail-reject`}
                onClick={() => setRejectMode(true)}
                className="text-rose-700 hover:bg-rose-50 hover:text-rose-700 dark:text-rose-400 dark:hover:bg-rose-500/10"
              >
                <X className="h-4 w-4" />
                Elutasítás
              </Button>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  Bezárás
                </Button>
                <Button
                  type="button"
                  data-testid={`${testIdPrefix}-detail-approve`}
                  onClick={() => onApprove(item)}
                  disabled={approving}
                >
                  {approving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4" />
                  )}
                  Jóváhagyás
                </Button>
              </div>
            </>
          ) : (
            <>
              {item.status === "waiting_approval" && (
                <Button
                  type="button"
                  variant="outline"
                  data-testid={`${testIdPrefix}-detail-delete`}
                  onClick={() => onDelete(item)}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                  Törlés
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Bezárás
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InfoRow({ label, wide, children }) {
  return (
    <div className={cn("space-y-1", wide && "sm:col-span-2")}>
      <p className="text-[10.5px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </p>
      <div className="text-[13.5px]">{children}</div>
    </div>
  );
}
